import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    Typography,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Category as CategoryIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';

interface Category {
    id: number;
    name: string;
    color: string;
    description?: string;
}

interface CategoryManagerProps {
    categories: Category[];
    onUpdate: () => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ categories, onUpdate }) => {
    const [open, setOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        color: '#1976d2',
        description: ''
    });

    const dataProvider = useDataProvider();
    const notify = useNotify();

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleOpenEditDialog = (category?: Category) => {
        if (category) {
            setCurrentCategory(category);
            setFormData({
                name: category.name,
                color: category.color,
                description: category.description || ''
            });
        } else {
            setCurrentCategory(null);
            setFormData({
                name: '',
                color: '#1976d2',
                description: ''
            });
        }
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setCurrentCategory(null);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            notify('Bitte geben Sie einen Namen ein', { type: 'warning' });
            return;
        }

        try {
            if (currentCategory) {
                // Update existing category
                await dataProvider.update('media_categories', {
                    id: currentCategory.id,
                    data: formData,
                    previousData: currentCategory
                });
                notify('Kategorie aktualisiert', { type: 'success' });
            } else {
                // Create new category
                await dataProvider.create('media_categories', {
                    data: formData
                });
                notify('Kategorie erstellt', { type: 'success' });
            }
            handleCloseEditDialog();
            onUpdate();
        } catch (error) {
            notify('Fehler beim Speichern', { type: 'error' });
        }
    };

    const handleDelete = async (categoryId: number) => {
        if (window.confirm('Möchten Sie diese Kategorie wirklich löschen?')) {
            try {
                await dataProvider.delete('media_categories', {
                    id: categoryId,
                    previousData: { id: categoryId }
                });
                notify('Kategorie gelöscht', { type: 'success' });
                onUpdate();
            } catch (error) {
                notify('Fehler beim Löschen', { type: 'error' });
            }
        }
    };

    return (
        <>
            <Button
                variant="outlined"
                startIcon={<CategoryIcon />}
                onClick={handleOpen}
                size="small"
            >
                Kategorien verwalten
            </Button>

            {/* Category List Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Kategorien verwalten</Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenEditDialog()}
                            size="small"
                        >
                            Neu
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <List>
                        {categories.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                                Noch keine Kategorien vorhanden
                            </Typography>
                        ) : (
                            categories.map((category) => (
                                <React.Fragment key={category.id}>
                                    <ListItem>
                                        <Chip
                                            label={category.name}
                                            sx={{
                                                backgroundColor: category.color,
                                                color: 'white',
                                                mr: 2
                                            }}
                                        />
                                        <ListItemText
                                            primary={category.name}
                                            secondary={category.description}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                onClick={() => handleOpenEditDialog(category)}
                                                sx={{ mr: 1 }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                onClick={() => handleDelete(category.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Schließen</Button>
                </DialogActions>
            </Dialog>

            {/* Edit/Create Dialog */}
            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {currentCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            fullWidth
                            autoFocus
                            required
                        />
                        <TextField
                            label="Beschreibung"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={2}
                        />
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Farbe
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <TextField
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    sx={{ width: 100 }}
                                />
                                <Chip
                                    label={formData.name || 'Vorschau'}
                                    sx={{
                                        backgroundColor: formData.color,
                                        color: 'white',
                                        fontSize: '1rem',
                                        height: 40,
                                        px: 2
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseEditDialog}>Abbrechen</Button>
                    <Button onClick={handleSave} variant="contained">
                        {currentCategory ? 'Aktualisieren' : 'Erstellen'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CategoryManager;
